import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import Header from "./app-header/Header";
import { API_ENDPOINTS } from "../constants/api";
import "@maersk-global/mds-components-core/mc-input";
import "@maersk-global/mds-components-core/mc-button";
import "@maersk-global/mds-components-core/mc-select";
import "@maersk-global/mds-components-core/mc-notification";
import "@maersk-global/mds-components-core/mc-modal";
import "@maersk-global/mds-components-core/mc-dialog";
import "@maersk-global/mds-components-core/mc-loading-indicator";
import "@maersk-global/mds-components-core/mc-card";
import "@maersk-global/mds-components-core/mc-tag";
import {
  McSelect,
  McOption,
  McInput,
  McButton,
  McModal,
  McDialog,
  McLoadingIndicator,
} from "@maersk-global/mds-react-wrapper";
import { McCard } from "@maersk-global/mds-react-wrapper/components-core/mc-card";
import { McTag } from "@maersk-global/mds-react-wrapper/components-core/mc-tag";
import "../styles/maersk-theme.css";
import "../styles/afr-pop-form.css";

const ACCEPTED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png"];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB


function AFRPopSubmissionForm({
  onSubmit,
  status,
  onGoToDashboard,
  toolResult,
  editMode,
  editedResult,
  onEditedResultChange,
  onFileUploaded,
  onFilesUploaded,
  onFileRemoved,
  onClearToolResult,
  strings,
  language,
  onLanguageChange,
  changesSummary = [],
  isVerifying = false,
  isConfirming = false,
  caseId = "",
  showEditFilePicker = false,
  onEditFilePickerClose = () => {},
  uploadedFiles = [],
  onFileSelectedForEdit = () => {},
  editedToolResult = null,
  viewToolResult = null,
  userName = "",
  userPhotoUrl = null,
  onLogout = () => {},
  verificationResult = null,
  onCancelEdit = () => {},
  onContinue = () => {},
  onSubmitForManualReview = () => {},
  onReset = () => {},
  s4Lookup = null,
  s4InvoiceNumber = "",
  caseIdsByFile = {},
  verifyingByFile = {},
  verificationResultsByFile = {},
  getToken = null,
}) {
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [fileError, setFileError] = useState(null);
  const [blNumber, setBlNumber] = useState("");
  const [blError, setBlError] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceError, setInvoiceError] = useState(null);
  const [atLeastOneError, setAtLeastOneError] = useState(null);
  const [country, setCountry] = useState("");
  const [type, setType] = useState("");
  const [editValues, setEditValues] = useState({});
  const [fileUrls, setFileUrls] = useState([]);
  const fileInputRef = useRef(null);
  const blInputRef = useRef(null);
  const invoiceInputRef = useRef(null);
  const blDropdownRef = useRef(null);
  const invoiceDropdownRef = useRef(null);
  const [blDropdownOpen, setBlDropdownOpen] = useState(false);
  const [invoiceDropdownOpen, setInvoiceDropdownOpen] = useState(false);
  const [openChangesDropdowns, setOpenChangesDropdowns] = useState({});
  const [submitted] = useState(false);
  const [emailText, setEmailText] = useState("");
  const [emailExtracting, setEmailExtracting] = useState(false);
  const [emailExtractResult, setEmailExtractResult] = useState(null);
  const [emailRequiredError, setEmailRequiredError] = useState(false);
  const [blCardVal, setBlCardVal] = useState("");
  const [invoiceCardVal, setInvoiceCardVal] = useState("");
  const [blCardEditing, setBlCardEditing] = useState(false);
  const [invoiceCardEditing, setInvoiceCardEditing] = useState(false);
  const [blCardSaved, setBlCardSaved] = useState(false);
  const [invoiceCardSaved, setInvoiceCardSaved] = useState(false);
  const [blOptions, setBlOptions] = useState([]);
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [proceedClicked, setProceedClicked] = useState(false);
  const [fieldEvals, setFieldEvals] = useState({});
  const [openCommentRows, setOpenCommentRows] = useState(new Set());
  const [fieldAccuracyStats, setFieldAccuracyStats] = useState({});
  const evalSaveTimers = useRef({});
  const fieldEvalsRef = useRef({});
  const defaultsSavedRef = useRef(null);
  // Per-file storage for the reference fields (bl, invoice, country, type)
  const fieldsByFileRef = useRef({});
  const currentFileKey = file ? `${file.name}-${file.size}-${file.lastModified}` : null;


  useEffect(() => {
    if (blInputRef.current && blInputRef.current.value !== blNumber) {
      blInputRef.current.value = blNumber;
    }
  }, [blNumber]);
  useEffect(() => {
    if (invoiceInputRef.current && invoiceInputRef.current.value !== invoiceNumber) {
      invoiceInputRef.current.value = invoiceNumber;
    }
  }, [invoiceNumber]);

  // Restore saved field values when the active file changes
  useEffect(() => {
    if (!currentFileKey) return;
    const saved = fieldsByFileRef.current[currentFileKey] || {};
    setBlNumber(saved.blNumber ?? "");
    setInvoiceNumber(saved.invoiceNumber ?? "");
    setCountry(saved.country ?? "");
    setType(saved.type ?? "");
    setBlError(null);
    setInvoiceError(null);
    setAtLeastOneError(null);
  }, [currentFileKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close BL / invoice dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (blDropdownRef.current && !blDropdownRef.current.contains(e.target)) setBlDropdownOpen(false);
      if (invoiceDropdownRef.current && !invoiceDropdownRef.current.contains(e.target)) setInvoiceDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Populate "Invoice from S4" field when the first verifyCase result arrives after proceedExtract
  useEffect(() => {
    if (!s4InvoiceNumber) return;
    setInvoiceNumber(s4InvoiceNumber);
    saveFieldForCurrentFile({ invoiceNumber: s4InvoiceNumber });
    if (invoiceInputRef.current) invoiceInputRef.current.value = s4InvoiceNumber;
  }, [s4InvoiceNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateFile = (selectedFile) => {
    if (!selectedFile) return "Please upload a Proof of Payment file.";
    const lowerName = selectedFile.name.toLowerCase();
    const hasValidExtension = ACCEPTED_EXTENSIONS.some((ext) =>
      lowerName.endsWith(ext)
    );
    if (!hasValidExtension)
      return "Invalid file type. Accepted: PDF, JPG, JPEG, PNG.";
    if (selectedFile.size > MAX_FILE_SIZE_BYTES)
      return "File is too large. Maximum size is 10 MB.";
    return null;
  };

  const validateAtLeastOneRequired = (bl, invoice) => {
    if (!bl.trim() && !invoice.trim()) return strings.blOrInvoiceRequired;
    return null;
  };

  const validateCommaSeparated = (value) => {
    const invalidCharsPattern = /[^\w,\-]/;
    if (value && invalidCharsPattern.test(value)) return strings.invalidCharacters;
    return null;
  };

  const handleFileChange = (event) => {
    const selectedFiles = event.target.files ? Array.from(event.target.files) : [];
    if (selectedFiles.length === 0) return;
    const validFiles = [];
    const errors = [];
    selectedFiles.forEach((f, index) => {
      const error = validateFile(f);
      if (error) errors.push(`File ${index + 1} (${f.name}): ${error}`);
      else validFiles.push(f);
    });
    if (errors.length > 0) {
      setFileError(errors.join("; "));
      return;
    }
    // Only add files not already in the list (deduplicate by name+size+lastModified)
    const newFiles = validFiles.filter(
      (f) => !files.some(
        (existing) => existing.name === f.name && existing.size === f.size && existing.lastModified === f.lastModified
      )
    );
    if (newFiles.length === 0) {
      setFileError("All selected files are already uploaded.");
      return;
    }
    const newUrls = newFiles.map((f) => URL.createObjectURL(f));
    const mergedFiles = [...files, ...newFiles];
    const mergedUrls = [...fileUrls, ...newUrls];
    setFileUrls(mergedUrls);
    setFiles(mergedFiles);
    if (!file) {
      setFile(mergedFiles[0]);
      setCurrentFileIndex(0);
    }
    setFileError(null);
    // Reset input so same file can be re-selected later if needed
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (onFilesUploaded) onFilesUploaded(newFiles);
  };

  const saveFieldForCurrentFile = (updates) => {
    if (!currentFileKey) return;
    fieldsByFileRef.current[currentFileKey] = {
      ...fieldsByFileRef.current[currentFileKey],
      ...updates,
    };
  };

  const handleBlChange = (event) => {
    const value = event.target.value;
    setBlNumber(value);
    setAtLeastOneError(null);
    setBlError(validateCommaSeparated(value));
    saveFieldForCurrentFile({ blNumber: value });
  };

  const handleInvoiceChange = (event) => {
    const value = event.target.value;
    setInvoiceNumber(value);
    setAtLeastOneError(null);
    setInvoiceError(validateCommaSeparated(value));
    saveFieldForCurrentFile({ invoiceNumber: value });
  };

  const handleSelectFile = (index) => {
    if (!files[index]) return;
    setCurrentFileIndex(index);
    setFile(files[index]);
    if (onFileUploaded) onFileUploaded(files[index]);
  };

  const handleRemoveFile = (event, index) => {
    event.stopPropagation();
    if (!files[index]) return;
    const removedFile = files[index];
    const removedKey = `${removedFile.name}-${removedFile.size}-${removedFile.lastModified}`;
    delete fieldsByFileRef.current[removedKey];
    if (fileUrls[index]) URL.revokeObjectURL(fileUrls[index]);
    const nextFiles = files.filter((_, i) => i !== index);
    const nextFileUrls = fileUrls.filter((_, i) => i !== index);
    setFiles(nextFiles);
    setFileUrls(nextFileUrls);
    if (onFileRemoved) onFileRemoved(removedFile);
    if (nextFiles.length === 0) {
      setFile(null);
      setCurrentFileIndex(0);
      if (onClearToolResult) onClearToolResult();
      return;
    }
    let nextIndex = currentFileIndex;
    if (index < currentFileIndex) nextIndex = currentFileIndex - 1;
    else if (index === currentFileIndex)
      nextIndex = Math.min(index, nextFiles.length - 1);
    setCurrentFileIndex(nextIndex);
    setFile(nextFiles[nextIndex]);
    if (onFileUploaded) onFileUploaded(nextFiles[nextIndex]);
  };

  const isFormValid = useMemo(() => {
    const atLeastOne = editMode ? null : validateAtLeastOneRequired(blNumber, invoiceNumber);
    const blVal = blNumber ? validateCommaSeparated(blNumber) : null;
    const invVal = invoiceNumber ? validateCommaSeparated(invoiceNumber) : null;
    const fileValidation = validateFile(file);
    return !fileValidation && !atLeastOne && !blVal && !invVal;
  }, [file, blNumber, invoiceNumber, editMode]);

  useEffect(() => {
    if (
      editMode &&
      toolResult &&
      typeof toolResult === "object" &&
      !Array.isArray(toolResult)
    ) {
      const initial = {};
      Object.entries(toolResult).filter(([k]) => !k.startsWith("_")).forEach(([key, value]) => {
        // Prefer previously saved edit for this key; fall back to extracted value.
        const savedEdit =
          editedToolResult &&
          typeof editedToolResult === "object" &&
          editedToolResult[key] !== undefined
            ? editedToolResult[key]
            : null;
        if (savedEdit !== null) {
          initial[key] = savedEdit; // already a string from a prior editing session
        } else if (value && typeof value === "object") {
          initial[key] = JSON.stringify(value, null, 2);
        } else {
          initial[key] = value == null ? "" : String(value);
        }
      });
      setEditValues(initial);
      if (onEditedResultChange) onEditedResultChange(initial);
    }
  }, [editMode, toolResult]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const fileValidation = validateFile(file);
    setFileError(fileValidation);
    if (fileValidation) return;
    const atLeastOneValidation = validateAtLeastOneRequired(blNumber, invoiceNumber);
    setAtLeastOneError(atLeastOneValidation);
    if (atLeastOneValidation) return;
    const nativeEvent = event.nativeEvent || {};
    const submitter = nativeEvent.submitter || {};
    const action = submitter.value || "confirm";
    const payload = {
      blNumber: blNumber.trim(),
      invoiceNumber: invoiceNumber.trim(),
      country,
      file,
    };
    if (onSubmit) onSubmit(payload, action);
  };

  const handleEditButtonClick = () => {
    const fileValidation = validateFile(file);
    setFileError(fileValidation);
    if (fileValidation) return;
    setAtLeastOneError(null);
    const payload = {
      blNumber: blNumber.trim(),
      invoiceNumber: invoiceNumber.trim(),
      country,
      file,
    };
    if (onSubmit) onSubmit(payload, "edit");
  };

  const handleEmailExtract = async () => {
    if (!emailText.trim()) return null;
    setEmailExtracting(true);
    setEmailExtractResult(null);
    setBlCardVal("");
    setInvoiceCardVal("");
    setBlCardSaved(false);
    setInvoiceCardSaved(false);
    setBlCardEditing(false);
    setInvoiceCardEditing(false);
    setProceedClicked(false);
    try {
      const token = getToken ? await getToken() : null;
      // eslint-disable-next-line no-control-regex
      const cleanText = emailText.replace(/[\x00-\x08\x0b\x0c\x0e-\x1f]/g, "");
      const res = await fetch(API_ENDPOINTS.extractEmail, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ email_text: cleanText }),
      });
      const data = await res.json();
      setEmailExtractResult(data);
      const bls = data.bl_number || [];
      const invs = data.invoice_number || [];
      setBlOptions(bls);
      setInvoiceOptions(invs);
      if (bls.length > 0) {
        setBlCardVal(bls.join(", "));
        if (bls.length > 1) { setBlNumber(bls[0]); saveFieldForCurrentFile({ blNumber: bls[0] }); }
      } else {
        setBlCardEditing(true);
      }
      if (invs.length > 0) {
        setInvoiceCardVal(invs.join(", "));
        if (invs.length > 1) { setInvoiceNumber(invs[0]); saveFieldForCurrentFile({ invoiceNumber: invs[0] }); }
      } else {
        setInvoiceCardEditing(true);
      }
      return data;
    } catch (err) {
      console.error("Email extract error:", err);
      setEmailExtractResult({ extraction_status: "not_found", reason: "Request failed. Check network or server." });
      return null;
    } finally {
      setEmailExtracting(false);
    }
  };

  const handleExtractButtonClick = () => {
    if (!emailText.trim()) {
      setEmailRequiredError(true);
      return;
    }
    setEmailRequiredError(false);
    const fileValidation = validateFile(file);
    setFileError(fileValidation);
    if (fileValidation) return;
    setAtLeastOneError(null);
    // Email extraction only — POP extraction happens on "Proceed for Extraction"
    handleEmailExtract();
  };

  const handleProceedForExtraction = () => {
    setProceedClicked(true);
    // When multiple options exist, blNumber/invoiceNumber already hold the dropdown-selected value — don't override with the comma string
    const bl = blOptions.length > 1 ? blNumber : blCardVal.trim();
    const inv = invoiceOptions.length > 1 ? invoiceNumber : invoiceCardVal.trim();
    if (blOptions.length <= 1 && blCardVal) { setBlNumber(blCardVal); saveFieldForCurrentFile({ blNumber: blCardVal }); }
    if (invoiceOptions.length <= 1 && invoiceCardVal) { setInvoiceNumber(invoiceCardVal); saveFieldForCurrentFile({ invoiceNumber: invoiceCardVal }); }
    const payload = { blNumber: bl, invoiceNumber: inv, country, type, file };
    if (onSubmit) onSubmit(payload, "proceedExtract");
  };

  const handleConfirmClick = () => {
    const payload = {
      currentFileData: {
        fileKey: currentFileKey,
        fileName: file?.name || "",
        blNumber: blNumber.trim(),
        invoiceNumber: invoiceNumber.trim(),
        country,
        type,
      },
    };
    if (onSubmit) onSubmit(payload, "confirm");
  };

  const handleSubmitAgain = () => {
    setEmailText("");
    setEmailExtractResult(null);
    setEmailRequiredError(false);
    setBlCardVal("");
    setInvoiceCardVal("");
    setBlCardSaved(false);
    setInvoiceCardSaved(false);
    setBlCardEditing(false);
    setInvoiceCardEditing(false);
    setBlOptions([]);
    setInvoiceOptions([]);
    setProceedClicked(false);
    if (onReset) onReset();
  };

  const handleValidateClick = () => {
    const payload = {
      fileKey: currentFileKey,
      fileName: file?.name || "",
      blNumber: blNumber.trim(),
      invoiceNumber: invoiceNumber.trim(),
      country,
      type,
      file,
    };
    if (onSubmit) onSubmit(payload, "validate");
  };

  const handleSaveButtonClick = () => {
    const payload = {
      blNumber: blNumber.trim(),
      invoiceNumber: invoiceNumber.trim(),
      file,
      country,
    };
    if (onSubmit) onSubmit(payload, "save");
  };

  // ── Field eval helpers ──────────────────────────────────────────
  const evalDocId = caseId || currentFileKey || "";

  // Keep a ref in sync so debounced saves can read latest rating
  useEffect(() => { fieldEvalsRef.current = fieldEvals; }, [fieldEvals]);

  // Load saved evals when the active document changes
  useEffect(() => {
    if (!evalDocId) { setFieldEvals({}); setOpenCommentRows(new Set()); return; }
    (async () => {
      try {
        const token = getToken ? await getToken() : null;
        const res = await fetch(API_ENDPOINTS.fieldEvalByDoc(evalDocId), {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (res.ok) {
          const data = await res.json();
          const loaded = {};
          (data.evals || []).forEach(e => { loaded[e.field_key] = { rating: e.rating || null, comment: e.comment || "" }; });
          setFieldEvals(loaded);
        }
      } catch { /* non-fatal */ }
    })();
  }, [evalDocId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveFieldEval = useCallback(async (fieldKey, rating, comment) => {
    if (!evalDocId) return;
    try {
      const token = getToken ? await getToken() : null;
      await fetch(API_ENDPOINTS.fieldEval, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ document_id: evalDocId, field_key: fieldKey, rating: rating || null, comment: comment || "" }),
      });
    } catch { /* non-fatal */ }
  }, [evalDocId, getToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAccuracyStats = useCallback(async () => {
    try {
      const token = getToken ? await getToken() : null;
      const res = await fetch(API_ENDPOINTS.fieldAccuracy, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (res.ok) setFieldAccuracyStats(await res.json());
    } catch { /* non-fatal */ }
  }, [getToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load accuracy stats on mount
  useEffect(() => { fetchAccuracyStats(); }, [fetchAccuracyStats]);

  // Save default "up" for every unrated field when extraction result first arrives
  useEffect(() => {
    if (!editMode || !toolResult || !evalDocId) return;
    if (defaultsSavedRef.current === evalDocId) return;
    defaultsSavedRef.current = evalDocId;
    const unrated = Object.keys(toolResult)
      .filter(k => !k.startsWith("_"))
      .filter(k => !fieldEvalsRef.current[k]?.rating);
    if (unrated.length === 0) return;
    unrated.forEach(k => saveFieldEval(k, "up", ""));
    fetchAccuracyStats();
  }, [editMode, toolResult, evalDocId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldRating = useCallback(async (fieldKey, dir) => {
    const cur = fieldEvalsRef.current[fieldKey] || { rating: null, comment: "" };
    const newRating = cur.rating === dir ? null : dir;
    setFieldEvals(prev => ({ ...prev, [fieldKey]: { ...cur, rating: newRating } }));
    await saveFieldEval(fieldKey, newRating, cur.comment);
    fetchAccuracyStats();
  }, [saveFieldEval, fetchAccuracyStats]);

  const handleFieldComment = useCallback((fieldKey, comment) => {
    setFieldEvals(prev => ({ ...prev, [fieldKey]: { ...(prev[fieldKey] || { rating: null, comment: "" }), comment } }));
    clearTimeout(evalSaveTimers.current[fieldKey]);
    evalSaveTimers.current[fieldKey] = setTimeout(() => {
      saveFieldEval(fieldKey, fieldEvalsRef.current[fieldKey]?.rating || null, comment);
    }, 500);
  }, [saveFieldEval]);

  const toggleCommentRow = useCallback((fieldKey) => {
    setOpenCommentRows(prev => { const n = new Set(prev); n.has(fieldKey) ? n.delete(fieldKey) : n.add(fieldKey); return n; });
  }, []);

  const formatLabel = (key) => {
    if (!key) return "";
    if (key === "payee_account_no") return "Payee acc no";
    const withSpaces = key.replace(/_/g, " ");
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  };

  const hasToolResultEdits = useMemo(() => {
    if (!editMode || !toolResult) return false;

    if (typeof toolResult === "object" && !Array.isArray(toolResult)) {
      return Object.entries(toolResult).some(([key, value]) => {
        if (key.startsWith("_")) return false;
        const originalValue =
          value && typeof value === "object"
            ? JSON.stringify(value, null, 2)
            : value == null
              ? ""
              : String(value);
        const editedValue = editValues[key] ?? "";
        return editedValue !== originalValue;
      });
    }

    return (editedResult || "") !== String(toolResult ?? "");
  }, [editMode, toolResult, editValues, editedResult]);

  const renderToolResultContent = (resultOverride) => {
    const result = resultOverride !== undefined ? resultOverride : toolResult;
    if (!result) {
      return (
        <span className="afr-pop-tool-result-placeholder">
          {strings.toolResultPlaceholder}
        </span>
      );
    }
    if (Array.isArray(result)) {
      return (
        <pre className="afr-pop-tool-result-pre">
          {JSON.stringify(result, null, 2)}
        </pre>
      );
    }
    if (typeof result === "object") {
      return (
        <div className="afr-pop-tool-result-grid">
          {Object.entries(result).filter(([k]) => !k.startsWith("_")).map(([key, value]) => (
            <div className="afr-pop-tool-row" key={key}>
              <div className="afr-pop-tool-label">{formatLabel(key)}</div>
              <div className="afr-pop-tool-value">
                {value && typeof value === "object" ? (
                  <pre className="afr-pop-tool-result-pre">
                    {JSON.stringify(value, null, 2)}
                  </pre>
                ) : (
                  String(value == null ? "" : value)
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }
    return (
      <span className="afr-pop-tool-result-placeholder">
        {String(result)}
      </span>
    );
  };

  const openFilePicker = () => fileInputRef.current?.click();

  const displayFileName =
    files.length > 0
      ? strings.filesSelected.replace("{count}", files.length)
      : "No file selected";
  const popCounterText =
    files.length > 1
      ? strings.popCounter
          .replace("{current}", currentFileIndex + 1)
          .replace("{total}", files.length)
      : "";
  const areMainActionsDisabled = false;

  return (
    <div className="afr-pop-page" data-success={status === "success" ? "true" : undefined}>
      <Header language={language} onLanguageChange={onLanguageChange} strings={strings} userName={userName} userPhotoUrl={userPhotoUrl} onLogout={onLogout} onGoToDashboard={onGoToDashboard} />
      {showEditFilePicker && uploadedFiles && uploadedFiles.length > 0 && (
        <McModal
          heading="Select File to Edit"
          open={showEditFilePicker}
          modalclosed={onEditFilePickerClose}
        >
          <div className="afr-pop-file-picker-modal">
            <p>Select which file you would like to edit:</p>
            <div className="afr-pop-file-picker-list">
              {uploadedFiles.map((f, index) => (
                <div
                  key={index}
                  className="afr-pop-file-picker-item"
                  onClick={() => onFileSelectedForEdit(f)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onFileSelectedForEdit(f);
                    }
                  }}
                >
                  <span className="afr-pop-file-picker-name">{f.name}</span>
                  <span className="afr-pop-file-picker-size">
                    {(f.size / 1024).toFixed(2)} KB
                  </span>
                </div>
              ))}
            </div>
          </div>
          <McButton
            slot="footer"
            label="Cancel"
            appearance="neutral"
            variant="outlined"
            click={onEditFilePickerClose}
          />
        </McModal>
      )}
      <main className="afr-pop-main scroll-region no-banner">
        {status === "success" ? (
          <div className="afr-success-page">
            {/* Hero card */}
            <div className="afr-success-hero">
              <div className="afr-success-check-circle">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="36" height="36">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h1 className="afr-success-heading">{strings.successTitle}</h1>
              <p className="afr-success-subtext">{strings.successText}</p>
              <div className="afr-success-pill-row">
                <span className="afr-success-pill afr-success-pill--green">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><polyline points="20 6 9 17 4 12" /></svg>
                  Status: Completed
                </span>
                {caseId && (
                  <span className="afr-success-pill afr-success-pill--blue">
                    Case ID: {caseId}
                  </span>
                )}
              </div>
            </div>

            {/* Submission summary card */}
            <div className="afr-success-summary-card">
              <div className="afr-success-summary-header">
                <span className="afr-success-summary-title">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                  Submission summary
                </span>
              </div>

              <div className="afr-success-file-list">
                {uploadedFiles && uploadedFiles.length > 0 ? (
                  uploadedFiles.map((file, idx) => {
                    const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
                    const fileChanges = changesSummary.find(c => c.fileKey === fileKey);
                    const isOpen = !!openChangesDropdowns[idx];
                    return (
                      <div key={idx} className="afr-success-file-row">
                        <div className="afr-success-file-main">
                          <div className="afr-success-file-left">
                            <div className="afr-success-file-icon">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                              </svg>
                            </div>
                            <span className="afr-success-file-name">{file.name}</span>
                          </div>
                          <div className="afr-success-file-right">
                            <button
                              className="afr-success-view-details"
                              onClick={() => setOpenChangesDropdowns(prev => ({ ...prev, [idx]: !prev[idx] }))}
                            >
                              View Details
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" style={{transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s"}}>
                                <polyline points="6 9 12 15 18 9"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="afr-success-progress-bar" />
                        {isOpen && fileChanges && (
                          <div className="afr-success-changes-dropdown">
                            {fileChanges.allFields.map((change, ci) => (
                              <div key={ci} className="afr-success-change-item">
                                <span className="afr-success-change-field">{formatLabel(change.field)}</span>
                                <span className={change.isChanged ? "afr-success-change-edited" : "afr-success-change-value"}>
                                  {String(change.edited ?? "—")}
                                </span>
                                {change.isChanged && (
                                  <span className="afr-success-change-tag">edited</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {isOpen && !fileChanges && (
                          <div className="afr-success-changes-dropdown">
                            <p className="afr-success-no-details">No extraction data available.</p>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="afr-success-no-files">No files were submitted.</p>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="afr-success-actions">
              {submitted ? (
                <div className="afr-submission-success-btn">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><polyline points="20 6 9 17 4 12"/></svg>
                  Submission Successful
                </div>
              ) : (
                <McButton
                  label="Go to Dashboard"
                  appearance="neutral"
                  variant="outlined"
                  click={onGoToDashboard}
                />
              )}
            </div>
          </div>
        ) : (
        <div className="afr-pop-two-col">
          <div className="afr-pop-side-card afr-pop-left-card">
            <div className="afr-pop-panel-content">
                <form
                  className="afr-pop-form"
                  onSubmit={handleSubmit}
                  noValidate
                >
                  <div className="afr-doc-extraction-banner">
                    {strings.subtitle || "Digitally analyze your documents with smart automation"}
                  </div>
                  <div className="afr-pop-tab-bar-with-lang">
                    <div className="afr-pop-tab-bar-cell">
                      <div className="afr-pop-tab-panel">

                  <div className="afr-pop-field">
                    <label className="afr-pop-label" htmlFor="pop-file">
                      {strings.popUploadLabel}
                      <span style={{color:"#d32f2f"}}> *</span>
                      {popCounterText && (
                        <span className="afr-pop-file-counter">
                          {" "}
                          - {popCounterText}
                        </span>
                      )}
                    </label>
                    <div className="afr-pop-file-input-wrapper">
                      <input
                        id="pop-file"
                        ref={fileInputRef}
                        type="file"
                        className="afr-pop-file-input"
                        accept={ACCEPTED_EXTENSIONS.join(",")}
                        onChange={handleFileChange}
                        multiple
                      />
                      <McButton
                        label={strings.chooseFiles || "Choose files"}
                        click={openFilePicker}
                      />
                      <div
                        className={
                          "afr-pop-file-input-display" +
                          (fileError ? " afr-pop-input-error" : "")
                        }
                      >
                        {files.length === 0 ? (
                          <span className="afr-pop-file-name">
                            {displayFileName}
                          </span>
                        ) : (
                          <div className="afr-pop-file-list">
                            {files.map((f, index) => (
                              <div
                                key={f.name + index}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleSelectFile(index)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleSelectFile(index);
                                  }
                                }}
                                className={
                                  "afr-pop-file-card" +
                                  (index === currentFileIndex
                                    ? " afr-pop-file-card-active"
                                    : "")
                                }
                                title={f.name}
                              >
                                <span className="afr-pop-file-card-name">
                                  {f.name}
                                </span>
                                {(() => {
                                  const fKey = `${f.name}-${f.size}-${f.lastModified}`;
                                  const fileCaseId = caseIdsByFile[fKey];
                                  const fileVerifying = !!verifyingByFile[fKey];
                                  const fileVR = verificationResultsByFile[fKey];
                                  const amountRule = fileVR ? (fileVR.rules || []).find(r => r.rule_name === "AMOUNT_VALIDATION") : null;
                                  if (fileVerifying) return (
                                    <span className="afr-file-status-badge afr-file-status-verifying">
                                      <span className="afr-file-spin">⟳</span>
                                    </span>
                                  );
                                  if (fileCaseId && amountRule) {
                                    const pass = amountRule.status === "Pass";
                                    return <span className={`afr-file-status-badge ${pass ? "afr-file-status-pass" : "afr-file-status-fail"}`}>{pass ? "✓" : "✗"}</span>;
                                  }
                                  if (fileCaseId) return <span className="afr-file-status-badge afr-file-status-confirmed">✓</span>;
                                  return null;
                                })()}
                                <button
                                  type="button"
                                  className="afr-pop-file-remove"
                                  onClick={(e) => handleRemoveFile(e, index)}
                                  aria-label={`Remove ${f.name}`}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {fileError && (
                      <span className="afr-pop-error-text" role="alert">
                        {fileError}
                      </span>
                    )}
                  </div>

                  {/* Email section — always visible below file picker */}
                  <div className="afr-email-input-section">
                      <label className="afr-pop-label">Paste email body / add relevant information <span style={{color:"#d32f2f"}}>*</span></label>
                      <textarea
                        className={`afr-email-textarea${emailRequiredError ? " afr-pop-input-error" : ""}`}
                        value={emailText}
                        onChange={e => { setEmailText(e.target.value); setEmailExtractResult(null); if (emailRequiredError) setEmailRequiredError(false); }}
                        placeholder="Paste the payment notification email here…"
                        rows={6}
                      />
                      {emailRequiredError && (
                        <span className="afr-pop-error-text" role="alert">
                          Please paste the email body before extracting.
                        </span>
                      )}
                      {emailExtracting && (
                        <div className="afr-pop-loading-container" style={{padding:"8px 0"}}>
                          <div className="afr-pop-spinner" />
                          <p className="afr-pop-loading-text">Extracting from email…</p>
                        </div>
                      )}
                      {!emailExtracting && emailExtractResult && (
                        <div className="afr-email-extract-result afr-email-inline-result">
                          {emailExtractResult.extraction_status === "not_found" ? (
                            <span className="afr-pop-tool-result-placeholder">No BL or invoice numbers found in the email.</span>
                          ) : (
                            <div className="afr-email-card-body-row">
                              {/* Left: field rows */}
                              <div className="afr-email-card-fields">
                                {/* BL Number */}
                                <div className="afr-email-result-row afr-email-card-field-row">
                                  <span className="afr-email-result-label">BL Number</span>
                                  <div className="afr-email-card-field-actions">
                                    {blCardEditing ? (
                                      <div className="afr-email-card-edit-row">
                                        <input className="afr-email-card-input" value={blCardVal}
                                          onChange={e => setBlCardVal(e.target.value)}
                                          placeholder="Enter BL number" autoFocus />
                                        <button type="button" className="afr-email-card-save-btn"
                                          onClick={() => { setBlCardSaved(true); setBlCardEditing(false); }}>Save</button>
                                        <button type="button" className="afr-email-card-cancel-btn"
                                          onClick={() => setBlCardEditing(false)}>Cancel</button>
                                      </div>
                                    ) : blCardSaved ? (
                                      <div className="afr-email-card-saved-row">
                                        <span className="afr-email-result-chip afr-email-result-chip--bl">{blCardVal || "—"}</span>
                                        <button type="button" className="afr-email-card-edit-icon-btn"
                                          onClick={() => setBlCardEditing(true)} title="Edit BL Number">
                                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        </button>
                                      </div>
                                    ) : blCardVal ? (
                                      <div className="afr-email-card-saved-row">
                                        <span className="afr-email-result-chip afr-email-result-chip--bl">{blCardVal}</span>
                                        <button type="button" className="afr-email-card-edit-icon-btn"
                                          onClick={() => setBlCardEditing(true)} title="Edit BL Number">
                                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        </button>
                                        <button type="button" className="afr-email-card-save-btn"
                                          onClick={() => setBlCardSaved(true)}>Save</button>
                                      </div>
                                    ) : (
                                      <button type="button" className="afr-email-card-add-btn"
                                        onClick={() => setBlCardEditing(true)}>+ Add</button>
                                    )}
                                  </div>
                                </div>

                                {/* Invoice Number */}
                                <div className="afr-email-result-row afr-email-card-field-row">
                                  <span className="afr-email-result-label">Invoice Number</span>
                                  <div className="afr-email-card-field-actions">
                                    {invoiceCardEditing ? (
                                      <div className="afr-email-card-edit-row">
                                        <input className="afr-email-card-input" value={invoiceCardVal}
                                          onChange={e => setInvoiceCardVal(e.target.value)}
                                          placeholder="Enter invoice number" autoFocus />
                                        <button type="button" className="afr-email-card-save-btn"
                                          onClick={() => { setInvoiceCardSaved(true); setInvoiceCardEditing(false); }}>Save</button>
                                        <button type="button" className="afr-email-card-cancel-btn"
                                          onClick={() => setInvoiceCardEditing(false)}>Cancel</button>
                                      </div>
                                    ) : invoiceCardSaved ? (
                                      <div className="afr-email-card-saved-row">
                                        <span className="afr-email-result-chip afr-email-result-chip--inv">{invoiceCardVal || "—"}</span>
                                        <button type="button" className="afr-email-card-edit-icon-btn"
                                          onClick={() => setInvoiceCardEditing(true)} title="Edit Invoice Number">
                                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        </button>
                                      </div>
                                    ) : invoiceCardVal ? (
                                      <div className="afr-email-card-saved-row">
                                        <span className="afr-email-result-chip afr-email-result-chip--inv">{invoiceCardVal}</span>
                                        <button type="button" className="afr-email-card-edit-icon-btn"
                                          onClick={() => setInvoiceCardEditing(true)} title="Edit Invoice Number">
                                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        </button>
                                        <button type="button" className="afr-email-card-save-btn"
                                          onClick={() => setInvoiceCardSaved(true)}>Save</button>
                                      </div>
                                    ) : (
                                      <button type="button" className="afr-email-card-add-btn"
                                        onClick={() => setInvoiceCardEditing(true)}>+ Add</button>
                                    )}
                                  </div>
                                </div>

                                {emailExtractResult.credit_note?.length > 0 && (
                                  <div className="afr-email-result-row">
                                    <span className="afr-email-result-label">Credit Note(s)</span>
                                    <div className="afr-email-result-chips">
                                      {emailExtractResult.credit_note.map((v, i) => (
                                        <span key={i} className="afr-email-result-chip afr-email-result-chip--cn">{v}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {emailExtractResult.reason && (
                                  <div className="afr-email-result-reason">{emailExtractResult.reason}</div>
                                )}
                              </div>

                              {/* Right: Proceed button — appears once both fields are saved */}
                              {blCardSaved && invoiceCardSaved && (
                                <div className="afr-email-card-proceed-col">
                                  <div className="afr-extract-black afr-extract-black--small">
                                    <McButton
                                      label="Proceed for Extraction"
                                      disabled={proceedClicked || status === "loading"}
                                      click={handleProceedForExtraction}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                  {(toolResult || status === "loading") && (
                  <div className="afr-pop-tool-result">
                    <div className="afr-pop-tool-result-header">
                      {strings.toolResultHeader}
                    </div>

                    {/* Reference fields — horizontal row above the result, only when a file is uploaded */}
                    {files.length > 0 && <div className="afr-pop-tool-fields-row">
                      <div className="afr-pop-tool-field-item">
                        <McSelect
                          label={strings.countryLabel}
                          placeholder={strings.countryPlaceholder || "Select country"}
                          value={country}
                          optionselected={(e) => {
                            const v = e.detail?.value;
                            if (v !== undefined) { setCountry(v); saveFieldForCurrentFile({ country: v }); }
                          }}
                        >
                          <McOption value="">{strings.countryPlaceholder || "Select country"}</McOption>
                          <McOption value="AR">Argentina</McOption>
                          <McOption value="AW">Aruba</McOption>
                          <McOption value="AU">Australia</McOption>
                          <McOption value="AT">Austria</McOption>
                          <McOption value="BS">Bahamas</McOption>
                          <McOption value="BE">Belgium</McOption>
                          <McOption value="BJ">Benin</McOption>
                          <McOption value="BO">Bolivia</McOption>
                          <McOption value="BR">Brazil</McOption>
                          <McOption value="BG">Bulgaria</McOption>
                          <McOption value="CA">Canada</McOption>
                          <McOption value="CM">Cameroon</McOption>
                          <McOption value="CL">Chile</McOption>
                          <McOption value="CO">Colombia</McOption>
                          <McOption value="CG">Congo</McOption>
                          <McOption value="CR">Costa Rica</McOption>
                          <McOption value="HR">Croatia</McOption>
                          <McOption value="CU">Cuba</McOption>
                          <McOption value="CW">Curacao</McOption>
                          <McOption value="CY">Cyprus</McOption>
                          <McOption value="CZ">Czech Republic</McOption>
                          <McOption value="CD">Democratic Republic of the Congo</McOption>
                          <McOption value="DK">Denmark</McOption>
                          <McOption value="DJ">Djibouti</McOption>
                          <McOption value="DO">Dominican Republic</McOption>
                          <McOption value="EC">Ecuador</McOption>
                          <McOption value="SV">El Salvador</McOption>
                          <McOption value="EE">Estonia</McOption>
                          <McOption value="FI">Finland</McOption>
                          <McOption value="FR">France</McOption>
                          <McOption value="GM">Gambia</McOption>
                          <McOption value="GE">Georgia</McOption>
                          <McOption value="DE">Germany</McOption>
                          <McOption value="XX">Global</McOption>
                          <McOption value="GR">Greece</McOption>
                          <McOption value="GT">Guatemala</McOption>
                          <McOption value="GN">Guinea</McOption>
                          <McOption value="GW">Guinea-Bissau</McOption>
                          <McOption value="GY">Guyana</McOption>
                          <McOption value="HT">Haiti</McOption>
                          <McOption value="HN">Honduras</McOption>
                          <McOption value="HU">Hungary</McOption>
                          <McOption value="IN">India</McOption>
                          <McOption value="ID">Indonesia</McOption>
                          <McOption value="IE">Ireland</McOption>
                          <McOption value="IL">Israel</McOption>
                          <McOption value="IT">Italy</McOption>
                          <McOption value="JM">Jamaica</McOption>
                          <McOption value="JP">Japan</McOption>
                          <McOption value="KE">Kenya</McOption>
                          <McOption value="KR">South Korea</McOption>
                          <McOption value="KW">Kuwait</McOption>
                          <McOption value="LB">Lebanon</McOption>
                          <McOption value="LR">Liberia</McOption>
                          <McOption value="LT">Lithuania</McOption>
                          <McOption value="LV">Latvia</McOption>
                          <McOption value="MY">Malaysia</McOption>
                          <McOption value="ML">Mali</McOption>
                          <McOption value="MR">Mauritania</McOption>
                          <McOption value="MX">Mexico</McOption>
                          <McOption value="MM">Myanmar</McOption>
                          <McOption value="NL">Netherlands</McOption>
                          <McOption value="NZ">New Zealand</McOption>
                          <McOption value="NI">Nicaragua</McOption>
                          <McOption value="NG">Nigeria</McOption>
                          <McOption value="NO">Norway</McOption>
                          <McOption value="OM">Oman</McOption>
                          <McOption value="PA">Panama</McOption>
                          <McOption value="PY">Paraguay</McOption>
                          <McOption value="PE">Peru</McOption>
                          <McOption value="PH">Philippines</McOption>
                          <McOption value="PL">Poland</McOption>
                          <McOption value="PT">Portugal</McOption>
                          <McOption value="PR">Puerto Rico</McOption>
                          <McOption value="QA">Qatar</McOption>
                          <McOption value="RO">Romania</McOption>
                          <McOption value="RW">Rwanda</McOption>
                          <McOption value="SA">Saudi Arabia</McOption>
                          <McOption value="SN">Senegal</McOption>
                          <McOption value="SG">Singapore</McOption>
                          <McOption value="SK">Slovakia</McOption>
                          <McOption value="SI">Slovenia</McOption>
                          <McOption value="ES">Spain</McOption>
                          <McOption value="LK">Sri Lanka</McOption>
                          <McOption value="SD">Sudan</McOption>
                          <McOption value="SR">Suriname</McOption>
                          <McOption value="SE">Sweden</McOption>
                          <McOption value="CH">Switzerland</McOption>
                          <McOption value="SY">Syria</McOption>
                          <McOption value="TW">Taiwan</McOption>
                          <McOption value="TZ">Tanzania</McOption>
                          <McOption value="TH">Thailand</McOption>
                          <McOption value="TT">Trinidad and Tobago</McOption>
                          <McOption value="TR">Turkey</McOption>
                          <McOption value="UG">Uganda</McOption>
                          <McOption value="UA">Ukraine</McOption>
                          <McOption value="AE">United Arab Emirates</McOption>
                          <McOption value="GB">United Kingdom</McOption>
                          <McOption value="UY">Uruguay</McOption>
                          <McOption value="US">USA</McOption>
                          <McOption value="VE">Venezuela</McOption>
                          <McOption value="VN">Vietnam</McOption>
                        </McSelect>
                      </div>
                      <div className="afr-pop-tool-field-item">
                        <McSelect
                          label={strings.typeLabel || "Type"}
                          placeholder={strings.typePlaceholder || "Select type"}
                          value={type}
                          optionselected={(e) => {
                            const v = e.detail?.value;
                            if (v !== undefined) { setType(v); saveFieldForCurrentFile({ type: v }); }
                          }}
                        >
                          <McOption value="">{strings.typePlaceholder || "Select type"}</McOption>
                          <McOption value="Import">Import</McOption>
                          <McOption value="Export">Export</McOption>
                        </McSelect>
                      </div>
                      <div className="afr-pop-tool-field-item">
                        <div className="afr-field-with-arrow" ref={blDropdownRef}>
                          <McInput
                            ref={blInputRef}
                            label={strings.blLabel}
                            placeholder={strings.blPlaceholder}
                            value={blNumber}
                            input={(e) => {
                              const v = e.target?.value;
                              if (v !== undefined) handleBlChange({ target: { value: v } });
                            }}
                          />
                          {blOptions.length > 1 && (
                            <>
                              <button
                                type="button"
                                className="afr-field-arrow-btn"
                                onClick={() => setBlDropdownOpen(o => !o)}
                                aria-label="Show BL options"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                                  <polyline points="6 9 12 15 18 9"/>
                                </svg>
                              </button>
                              {blDropdownOpen && (
                                <div className="afr-field-dropdown-panel">
                                  {blOptions.map(opt => (
                                    <div
                                      key={opt}
                                      className={`afr-field-dropdown-option${blNumber === opt ? " afr-field-dropdown-option--active" : ""}`}
                                      onMouseDown={() => { handleBlChange({ target: { value: opt } }); setBlDropdownOpen(false); }}
                                    >{opt}</div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        {blError && (
                          <span className="afr-pop-error-text" role="alert">{blError}</span>
                        )}
                      </div>
                      <div className="afr-pop-tool-field-item">
                        <div className="afr-field-with-arrow" ref={invoiceDropdownRef}>
                          <McInput
                            ref={invoiceInputRef}
                            label="Invoice from S4"
                            placeholder={strings.invoicePlaceholder}
                            value={invoiceNumber}
                            input={(e) => {
                              const v = e.target?.value;
                              if (v !== undefined) handleInvoiceChange({ target: { value: v } });
                            }}
                          />
                          {invoiceOptions.length > 1 && (
                            <>
                              <button
                                type="button"
                                className="afr-field-arrow-btn"
                                onClick={() => setInvoiceDropdownOpen(o => !o)}
                                aria-label="Show invoice options"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                                  <polyline points="6 9 12 15 18 9"/>
                                </svg>
                              </button>
                              {invoiceDropdownOpen && (
                                <div className="afr-field-dropdown-panel">
                                  {invoiceOptions.map(opt => (
                                    <div
                                      key={opt}
                                      className={`afr-field-dropdown-option${invoiceNumber === opt ? " afr-field-dropdown-option--active" : ""}`}
                                      onMouseDown={() => { handleInvoiceChange({ target: { value: opt } }); setInvoiceDropdownOpen(false); }}
                                    >{opt}</div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        {invoiceError && (
                          <span className="afr-pop-error-text" role="alert">{invoiceError}</span>
                        )}
                      </div>
                    </div>}

                    {atLeastOneError && (
                      <div style={{ margin: "8px 0 4px 0" }}>
                        <span className="afr-pop-error-text" role="alert">{atLeastOneError}</span>
                      </div>
                    )}

                    {files.length > 0 && (
                      <div className="afr-pop-tool-file-bar">
                        <McButton
                          label={file ? file.name : ""}
                          appearance="neutral"
                          variant="text"
                          click={() => {
                            if (file) {
                              const fileUrl = URL.createObjectURL(file);
                              window.open(fileUrl, "_blank");
                            }
                          }}
                          title={file ? `View/Download: ${file.name}` : ""}
                          className="afr-pop-tool-file-name-btn"
                        />
                        <div className="afr-pop-tool-file-buttons">
                          {currentFileIndex > 0 && (
                            <McButton
                              label={strings.previousButton}
                              appearance="neutral"
                              variant="outlined"
                              click={() =>
                                handleSelectFile(currentFileIndex - 1)
                              }
                              className="afr-pop-tool-prev-btn"
                            />
                          )}
                          {currentFileIndex < files.length - 1 && (
                            <McButton
                              label={strings.nextButton}
                              appearance="neutral"
                              variant="outlined"
                              click={() =>
                                handleSelectFile(currentFileIndex + 1)
                              }
                              className="afr-pop-tool-next-btn"
                            />
                          )}
                        </div>
                      </div>
                    )}
                    {status === "loading" && (
                      <div className="afr-pop-loading-container">
                        <div className="afr-pop-spinner" />
                        <p className="afr-pop-loading-text">
                          POP details are getting extracted
                        </p>
                      </div>
                    )}
                    {editMode && status !== "loading" ? (
                      <div className="afr-pop-tool-result-split">
                        {/* Left — extracted values (read-only) */}
                        <div className="afr-pop-tool-result-pane afr-pop-tool-result-readonly">
                          <div className="afr-eval-panel-header">Extracted</div>
                          {renderToolResultContent()}
                        </div>
                        {/* Right — editable values + eval controls */}
                        <div className="afr-pop-tool-result-pane afr-pop-tool-result-editable">
                          {toolResult && typeof toolResult === "object" && !Array.isArray(toolResult) ? (
                            <div className="afr-pop-tool-result-grid afr-pop-tool-result-grid-edit">
                              {/* Column headers */}
                              <div className="afr-eval-col-headers">
                                <span className="afr-eval-col-hdr afr-eval-col-hdr--value">Value</span>
                                <span className="afr-eval-col-hdr afr-eval-col-hdr--eval">Eval</span>
                                <span className="afr-eval-col-hdr afr-eval-col-hdr--note">Note</span>
                              </div>
                              {Object.entries(toolResult).filter(([k]) => !k.startsWith("_")).map(([key]) => {
                                const ev = fieldEvals[key] || { rating: null, comment: "" };
                                const commentOpen = openCommentRows.has(key);
                                const hasComment = ev.comment && ev.comment.trim().length > 0;
                                return (
                                  <div className="afr-eval-field-block" key={key}>
                                    <div className="afr-eval-field-row">
                                      <input
                                        className="afr-pop-tool-edit-input afr-eval-input"
                                        value={editValues[key] ?? ""}
                                        placeholder={formatLabel(key)}
                                        onChange={(e) => {
                                          const next = { ...editValues, [key]: e.target.value };
                                          setEditValues(next);
                                          if (onEditedResultChange) onEditedResultChange(next);
                                        }}
                                      />
                                      <button type="button" className={`afr-eval-thumb-btn${ev.rating === "up" ? " active-up" : ""}`}
                                        onClick={() => handleFieldRating(key, "up")} title="Good extraction">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                                      </button>
                                      <button type="button" className={`afr-eval-thumb-btn${ev.rating === "down" ? " active-down" : ""}`}
                                        onClick={() => handleFieldRating(key, "down")} title="Poor extraction">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/><path d="M17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg>
                                      </button>
                                      <button type="button" className={`afr-eval-comment-btn${commentOpen || hasComment ? " active" : ""}`}
                                        onClick={() => toggleCommentRow(key)} title="Add note">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                                      </button>
                                    </div>
                                    {commentOpen && (
                                      <div className="afr-eval-comment-row">
                                        <textarea
                                          className="afr-eval-comment-textarea"
                                          value={ev.comment}
                                          onChange={e => handleFieldComment(key, e.target.value)}
                                          placeholder={`Note for ${formatLabel(key)}…`}
                                          rows={2}
                                        />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <textarea
                              className="afr-pop-input afr-pop-tool-result-textarea"
                              value={editedResult || ""}
                              onChange={(e) => onEditedResultChange?.(e.target.value)}
                              placeholder="Update extracted data here"
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="afr-pop-tool-result-body">
                        {renderToolResultContent(viewToolResult !== null ? viewToolResult : undefined)}
                      </div>
                    )}
                  </div>
                  )}

                  {/* Submission confirmed banner — shown when caseId is set */}
                  {caseId && (
                    <div className="afr-pop-confirmed-banner">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      Submission confirmed — Case ID: <strong>{caseId}</strong>
                    </div>
                  )}

                  {/* Validating loader — shown while GCSS/S4 lookup + rules are running */}
                  {caseId && isVerifying && (
                    <div className="afr-pop-validating-loader">
                      <div className="afr-pop-validating-spinner" />
                      <span className="afr-pop-validating-text">Validating</span>
                      <span className="afr-pop-validating-dots">
                        <span /><span /><span />
                      </span>
                    </div>
                  )}

                  {/* Validation Summary — shown below tool result after verify */}
                  {(() => {
                    const amountRule = (verificationResult?.rules || []).find(r => r.rule_name === "AMOUNT_VALIDATION");
                    if (!amountRule) return null;
                    const isMatch = amountRule.status === "Pass";
                    const extractedStr = amountRule.afr_data || "—";
                    const invoiceStr = amountRule.booking_data || "—";
                    const parseAmountStr = (str) => {
                      const parts = (str || "").replace(/[()]/g, "").trim().split(/\s+/);
                      // If only one token and it's a number, there's no currency prefix
                      if (parts.length === 1 || !isNaN(parseFloat(parts[0]))) {
                        return { currency: "", amount: parseFloat(parts[0]) || 0 };
                      }
                      return { currency: parts[0] || "", amount: parseFloat(parts[1]) || 0 };
                    };
                    const ext = parseAmountStr(extractedStr);
                    const inv = parseAmountStr(invoiceStr);
                    const diff = ext.amount - inv.amount;
                    const diffPct = inv.amount !== 0 ? (diff / inv.amount * 100) : 0;
                    const diffCurrency = ext.currency || inv.currency;
                    return (
                      <div className="afr-val-summary-card">
                        <div className="afr-val-summary-header">
                          <span className="afr-val-summary-title">Validation Summary</span>
                          <span className={`afr-val-badge ${isMatch ? "afr-val-badge--match" : "afr-val-badge--mismatch"}`}>
                            {isMatch ? "MATCH" : "MISMATCH"}
                          </span>
                        </div>
                        <hr className="afr-val-divider" />
                        <div className="afr-val-amounts">
                          <div className="afr-val-amount-box">
                            <div className="afr-val-amount-label">EXTRACTED NET COST (FROM DOCUMENT)</div>
                            <div className="afr-val-amount-value">{extractedStr}</div>
                          </div>
                          <div className="afr-val-amount-box">
                            <div className="afr-val-amount-label">CALCULATED TOTAL COST (FROM S4)</div>
                            <div className="afr-val-amount-value">{invoiceStr}</div>
                          </div>
                          <div className="afr-val-amount-box afr-val-amount-box--diff">
                            <div className="afr-val-amount-label">DIFFERENCE</div>
                            <div className="afr-val-amount-value">
                              {diffCurrency ? `${diffCurrency} ` : ""}{Math.abs(diff).toFixed(2)}
                              <span className="afr-val-diff-pct">
                                ({diffPct >= 0 ? "+" : ""}{diffPct.toFixed(2)}%)
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Validation result card — pass or fail */}
                  {(() => {
                    const amountRule = (verificationResult?.rules || []).find(r => r.rule_name === "AMOUNT_VALIDATION");
                    if (!amountRule) return null;
                    if (amountRule.status === "Pass") {
                      return (
                        <div className="afr-val-success-card">
                          <div className="afr-val-success-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
                              <circle cx="12" cy="12" r="10"/>
                              <polyline points="9 12 11 14 15 10"/>
                            </svg>
                          </div>
                          <div className="afr-val-success-text">
                            <span className="afr-val-success-title">Validation Successful</span>
                            <span className="afr-val-success-sub">Amount matches the invoice. Your submission is ready to proceed.</span>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div className="afr-val-fail-card">
                        <div className="afr-val-fail-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="15" y1="9" x2="9" y2="15"/>
                            <line x1="9" y1="9" x2="15" y2="15"/>
                          </svg>
                        </div>
                        <div className="afr-val-fail-text">
                          <span className="afr-val-fail-title">Validation Unsuccessful</span>
                          <span className="afr-val-fail-sub">Amount does not match the invoice. Please review and submit for manual review.</span>
                        </div>
                        <McButton
                          label="Submit for Manual Review"
                          appearance="primary"
                          click={onSubmitForManualReview}
                          className="afr-val-fail-btn"
                        />
                      </div>
                    );
                  })()}

                  {/* Changes Summary — shown when there are edited fields */}
                  {(() => {
                    const allChanged = (changesSummary || []).flatMap(fc =>
                      (fc.allFields || [])
                        .filter(f => f.isChanged)
                        .map(f => ({ ...f, fileName: fc.fileName }))
                    );
                    if (allChanged.length === 0) return null;
                    return (
                      <>
                        <div className="afr-changes-summary-card">
                          <div className="afr-changes-summary-title">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                            Edited Values
                          </div>
                          <div className="afr-changes-summary-rows">
                            {allChanged.map((f, i) => (
                              <div key={i} className="afr-changes-summary-row">
                                <span className="afr-changes-field">{formatLabel(f.field)}</span>
                                <span className="afr-changes-original">{String(f.original ?? "—")}</span>
                                <svg viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11" style={{flexShrink:0}}>
                                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                                </svg>
                                <span className="afr-changes-edited">{String(f.edited ?? "—")}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <p className="afr-changes-warning">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13" style={{flexShrink:0}}>
                            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                          </svg>
                          Edited values will be subject to additional verification and may require manual review before approval.
                        </p>
                      </>
                    );
                  })()}

                  </div>
                    </div>
                  </div>

                  {files.length > 0 && (
                  <div className="afr-pop-actions">
                    {caseId ? (
                      <>
                        {/* Edit button intentionally removed after Confirm and Validate */}
                        {verificationResult ? (
                          <>
                            <McButton
                              label="Submit Again"
                              appearance="neutral"
                              variant="outlined"
                              click={handleSubmitAgain}
                            />
                            <McButton
                              label="Continue"
                              disabled={(() => {
                                const amountRule = (verificationResult?.rules || []).find(r => r.rule_name === "AMOUNT_VALIDATION");
                                return !!amountRule && amountRule.status !== "Pass";
                              })()}
                              click={onContinue}
                            />
                          </>
                        ) : isVerifying ? (
                          <span className="afr-pop-validating-text" style={{alignSelf:"center",fontSize:"0.85rem",color:"#607d8b"}}>Validating…</span>
                        ) : null}
                      </>
                    ) : toolResult ? (
                      editMode && hasToolResultEdits ? (
                        <>
                          <McButton
                            label="Save"
                            disabled={areMainActionsDisabled}
                            click={handleSaveButtonClick}
                          />
                          <McButton
                            label="Cancel"
                            appearance="neutral"
                            variant="outlined"
                            click={onCancelEdit}
                          />
                        </>
                      ) : (
                        <>
                          {!editMode && (
                            <McButton
                              label={strings.editButton}
                              appearance="neutral"
                              variant="outlined"
                              click={handleEditButtonClick}
                            />
                          )}
                          <McButton
                            label="Confirm and Validate"
                            disabled={areMainActionsDisabled || isConfirming || isVerifying}
                            click={handleValidateClick}
                          />
                          {editMode && (
                            <McButton
                              label="Cancel"
                              appearance="neutral"
                              variant="outlined"
                              click={onCancelEdit}
                            />
                          )}
                        </>
                      )
                    ) : (
                      <div className="afr-extract-black">
                        <McButton
                          label="Extract"
                          disabled={areMainActionsDisabled || status === "loading" || emailExtracting}
                          click={handleExtractButtonClick}
                        />
                      </div>
                    )}
                  </div>
                  )}
                </form>
            </div>
          </div>

          <div className="afr-pop-side-card afr-pop-right-card">
            <div className="afr-pop-panel-content">
              <div className="afr-pop-tab-panel">
                {files.length > 0 ? (
                    <div className="afr-pop-file-preview-container">
                      <div className="afr-pop-file-preview-header">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                        </svg>
                        <span className="afr-pop-file-preview-name" title={file?.name}>{file?.name}</span>
                      </div>
                      <div className="afr-pop-file-preview-body">
                        {(() => {
                          const safeUrl = typeof fileUrls[currentFileIndex] === "string" && fileUrls[currentFileIndex].startsWith("blob:") ? fileUrls[currentFileIndex] : "";
                          return file?.name?.toLowerCase().match(/\.(jpg|jpeg|png)$/) ? (
                            <img
                              src={safeUrl}
                              alt="Document preview"
                              className="afr-pop-file-preview-img"
                            />
                          ) : (
                            <embed
                              src={safeUrl}
                              type="application/pdf"
                              className="afr-pop-file-preview-iframe"
                            />
                          );
                        })()}
                      </div>
                    </div>
                  ) : (
                  <div className="afr-pop-instructions-sidebar">
                    <div className="afr-pop-instructions-title">How to use this tool</div>
                    <ol className="afr-pop-instructions-list">
                      <li><strong>Upload your file</strong> — Click "Choose Files" to upload a PDF, JPG, or PNG (max 10 MB). Multiple files supported.</li>
                      <li><strong>Fill in the reference fields</strong> — Select country, type, and enter at least one of BL Number or Invoice Number.</li>
                      <li><strong>Review extracted data</strong> — The tool automatically extracts payment details from your document.</li>
                      <li><strong>Edit if needed</strong> — Click "Edit" to correct any extracted values, then "Save" to apply changes.</li>
                      <li><strong>Confirm submission</strong> — Click "Confirm" to submit.</li>
                    </ol>
                  </div>
                  )}
              </div>
            </div>
          </div>
        </div>
        )}
      </main>
    </div>
  );
}

export default AFRPopSubmissionForm;
